import React from 'react';
import Dropdown from '../../../../components/Dropdown';
import { DropdownItem } from '../../../../components/Dropdown/config';
import { ratingBadge } from '../../badges';
import {
  CorFormData,
  CorFormErrors,
  COR_TYPE_OPTIONS,
  COR_STATUS_OPTIONS,
  COR_APPROACH_OPTIONS,
  LIKELIHOOD_OPTIONS,
  CONSEQUENCE_OPTIONS,
  corDimensionFields,
  riskBand,
} from '../../config';

interface TeamMember {
  id: number | string;
  first_name: string;
  last_name: string;
}

interface CorFormFieldsProps {
  formData: CorFormData;
  onChange: <K extends keyof CorFormData>(field: K, value: CorFormData[K]) => void;
  /** Management-role team members (for Raised By + Owner). */
  teamMembers: TeamMember[];
  errors?: CorFormErrors;
  required?: boolean;
  /** Read-only mode (QA accounts) — disables every control. */
  disabled?: boolean;
}

const toItems = (opts: { value: string; label: string }[]): DropdownItem[] =>
  opts.map(o => ({ id: o.value, label: o.label, value: o.value }));

const CorFormFields: React.FC<CorFormFieldsProps> = ({
  formData,
  onChange,
  teamMembers,
  errors = {},
  required = false,
  disabled = false,
}) => {
  const req = required ? ' *' : '';

  const memberItems: DropdownItem[] = teamMembers.map(tm => ({
    id: tm.id,
    label: `${tm.first_name} ${tm.last_name}`,
    value: tm.id.toString(),
  }));
  const statusItems = toItems(COR_STATUS_OPTIONS.map(s => ({ value: s, label: s })));
  const approachItems = toItems(COR_APPROACH_OPTIONS.map(s => ({ value: s, label: s })));
  const likelihoodItems = toItems(LIKELIHOOD_OPTIONS);
  const consequenceItems = toItems(CONSEQUENCE_OPTIONS);

  const num = (s: string): number | null => (s ? parseInt(s, 10) : null);
  const initScore = num(formData.likelihood) != null && num(formData.consequence) != null
    ? (num(formData.likelihood)! * num(formData.consequence)!) : null;
  const residScore = num(formData.residual_likelihood) != null && num(formData.residual_consequence) != null
    ? (num(formData.residual_likelihood)! * num(formData.residual_consequence)!) : null;

  return (
    <fieldset className="cor-fieldset" disabled={disabled}>
      {/* ============ GENERAL ============ */}
      <div className="form-section">
        <h3 className="section-title">General</h3>

        <div className="form-group">
          <label>Item Type{req}</label>
          <div className="cor-type-selector">
            {COR_TYPE_OPTIONS.map(option => (
              <label key={option} className="radio-option">
                <input
                  type="radio"
                  name="corType"
                  value={option}
                  checked={formData.type === option}
                  onChange={() => onChange('type', option)}
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="cor-title">Title{req}</label>
          <input
            id="cor-title"
            type="text"
            value={formData.title}
            onChange={(e) => onChange('title', e.target.value)}
            placeholder="Short descriptive title"
            className={errors.title ? 'error' : ''}
          />
          {errors.title && <div className="error-message">{errors.title}</div>}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Raised By</label>
            <Dropdown
              items={memberItems}
              value={formData.raised_by}
              onChange={(value) => onChange('raised_by', value)}
              placeholder="Select team member"
              searchable
              disabled={disabled}
            />
          </div>
          <div className="form-group">
            <label htmlFor="cor-date-raised">Date Raised</label>
            <input
              id="cor-date-raised"
              type="date"
              value={formData.date_raised}
              onChange={(e) => onChange('date_raised', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* ============ DESCRIPTION — three dimensions, primary type first ============ */}
      <div className="form-section">
        <h3 className="section-title">Description</h3>
        {corDimensionFields(formData.type).map((dim) => (
          <div className="form-group" key={dim.key}>
            <label htmlFor={`cor-${dim.key}`}>{dim.label}{dim.primary ? req : ''}</label>
            <textarea
              id={`cor-${dim.key}`}
              value={formData[dim.key]}
              onChange={(e) => onChange(dim.key, e.target.value)}
              placeholder={dim.placeholder}
              rows={dim.primary ? 3 : 2}
              className={errors[dim.key] ? 'error' : ''}
            />
            {errors[dim.key] && <div className="error-message">{errors[dim.key]}</div>}
          </div>
        ))}
      </div>

      {/* ============ RISK ASSESSMENT ============ */}
      <div className="form-section">
        <h3 className="section-title">Risk Assessment</h3>
        <div className="form-row">
          <div className="form-group">
            <label>Likelihood</label>
            <Dropdown
              items={likelihoodItems}
              value={formData.likelihood}
              onChange={(value) => onChange('likelihood', value)}
              placeholder="1–5"
              disabled={disabled}
            />
          </div>
          <div className="form-group">
            <label>Consequence</label>
            <Dropdown
              items={consequenceItems}
              value={formData.consequence}
              onChange={(value) => onChange('consequence', value)}
              placeholder="1–5"
              disabled={disabled}
            />
          </div>
          <div className="form-group">
            <label>Initial Risk Rating</label>
            <div className="cor-rating-display">{ratingBadge(riskBand(initScore), initScore)}</div>
          </div>
        </div>
        <div className="form-group">
          <label>Management Approach (5T's)</label>
          <Dropdown
            items={approachItems}
            value={formData.management_approach}
            onChange={(value) => onChange('management_approach', value)}
            placeholder="Tolerate / Treat / Track / Transfer / Terminate"
            disabled={disabled}
          />
        </div>
      </div>

      {/* ============ ACTIONS ============ */}
      <div className="form-section">
        <h3 className="section-title">Actions</h3>
        <div className="form-group">
          <label htmlFor="cor-controls">Controls / Actions</label>
          <textarea
            id="cor-controls"
            value={formData.controls_actions}
            onChange={(e) => onChange('controls_actions', e.target.value)}
            placeholder="Specific actions to be taken..."
            rows={2}
          />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Owner</label>
            <Dropdown
              items={memberItems}
              value={formData.owner}
              onChange={(value) => onChange('owner', value)}
              placeholder="Select owner"
              searchable
              disabled={disabled}
            />
          </div>
          <div className="form-group">
            <label htmlFor="cor-target-date">Target Date</label>
            <input
              id="cor-target-date"
              type="date"
              value={formData.target_date}
              onChange={(e) => onChange('target_date', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Status</label>
            <Dropdown
              items={statusItems}
              value={formData.status}
              onChange={(value) => onChange('status', value)}
              placeholder="Select status"
              disabled={disabled}
            />
          </div>
        </div>
      </div>

      {/* ============ POST-CONTROL RE-ASSESSMENT ============ */}
      <div className="form-section">
        <h3 className="section-title">Post-Control Re-assessment</h3>
        <div className="form-row">
          <div className="form-group">
            <label>Residual Likelihood</label>
            <Dropdown
              items={likelihoodItems}
              value={formData.residual_likelihood}
              onChange={(value) => onChange('residual_likelihood', value)}
              placeholder="1–5"
              disabled={disabled}
            />
          </div>
          <div className="form-group">
            <label>Residual Consequence</label>
            <Dropdown
              items={consequenceItems}
              value={formData.residual_consequence}
              onChange={(value) => onChange('residual_consequence', value)}
              placeholder="1–5"
              disabled={disabled}
            />
          </div>
          <div className="form-group">
            <label>Residual Rating</label>
            <div className="cor-rating-display">{ratingBadge(riskBand(residScore), residScore)}</div>
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="cor-closure">Closure Notes</label>
          <textarea
            id="cor-closure"
            value={formData.closure_notes}
            onChange={(e) => onChange('closure_notes', e.target.value)}
            placeholder="Outcome and effectiveness commentary at closure..."
            rows={2}
          />
        </div>
      </div>
    </fieldset>
  );
};

export default CorFormFields;
